// @ts-expect-error untyped module
import GenericError from '../error/GenericError';
// @ts-expect-error untyped module
import { push } from '../../utils/history';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { H3 } from 'foundations-components/transitional/components/H3';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import Raven from 'raven-js';
import { useCallback, useContext, useMemo } from 'react';
import { useGetUserInsightsQuery } from '../../../__generated__/chirp/com/hubspot/marketplace/insights/rpc/UserInsightsChirpService.hooks';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { fromFilterStateToLocation } from '../../utils/storefrontFilters';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RECOMMENDATION_PRIORITY = {
  country: 'country',
  industry: 'industry',
  language: 'language'
};
const RecommendationCard = ({
  recommendationKey,
  recommendationValue,
  onClick
}) => {
  return /*#__PURE__*/_jsx(CardWrapper, {
    compact: true,
    children: /*#__PURE__*/_jsx(Clickable, {
      "data-test-id": `${recommendationKey}-recommendation-card`,
      onClick: onClick,
      children: /*#__PURE__*/_jsx(CardSection, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "start",
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            "data-test-id": `${recommendationKey}-card-title`,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystem-marketplace-solutions-ui.homepage.recommendations.card.${recommendationKey}.title`,
              options: {
                [recommendationKey]: recommendationValue
              }
            })
          }), /*#__PURE__*/_jsx(Badge, {
            use: "beta",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.card.recommended"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: `ecosystem-marketplace-solutions-ui.homepage.recommendations.card.${recommendationKey}.subtitle`,
            options: {
              [recommendationKey]: recommendationValue
            }
          })]
        })
      })
    })
  });
};
export default function HomepageRecommendations() {
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const {
    data: insightsData,
    loading: insightsLoading,
    error: insightsError
  } = useGetUserInsightsQuery();
  const normalizedUserInsights = useMemo(() => {
    const insights = insightsData;
    // flatten user insights
    // and set country to ipCountry *only* if no hqCountry already exists
    return insights && Object.assign({
      country: insights.portalInsights && insights.portalInsights.hqCountry || insights.ipCountry
    }, insights.portalInsights);
  }, [insightsData]);
  const buildRecommendationTranslationKey = useCallback((recommendationKey, recommendationValue) => {
    let translationKey = '';
    switch (recommendationKey) {
      case RECOMMENDATION_PRIORITY.country:
        translationKey = `SharedI18nStrings.countryCodes.${recommendationValue}`;
        break;
      case RECOMMENDATION_PRIORITY.industry:
        translationKey = `marketplace-ui-common.industry.${recommendationValue}.sentence`;
        break;
      case RECOMMENDATION_PRIORITY.language:
        translationKey = `SharedI18nStrings.languageNames.${recommendationValue}`;
        break;
      default:
        break;
    }
    if (!isValidI18nKey(translationKey)) {
      Raven.captureMessage(`Invalid ${recommendationKey} translation - ${translationKey}`, {
        level: 'warning',
        extra: {
          value: recommendationValue
        }
      });
      return null;
    }
    return translationKey;
  }, []);
  const renderedRecommendationCards = useMemo(() => {
    return normalizedUserInsights && Object.keys(RECOMMENDATION_PRIORITY).reduce((renderedRecommendations, recommendationKey) => {
      const value = recommendationKey === RECOMMENDATION_PRIORITY.language ? getLang() : normalizedUserInsights[recommendationKey];

      // if there's no value associated with the key,
      // there's no reason to calculate the rest - return null
      // filter it out later
      if (!value) {
        return renderedRecommendations;
      }
      const translationKey = value && buildRecommendationTranslationKey(recommendationKey, value);
      const parsedRecommendation = {
        recommendationKey,
        recommendationTranslatedValue: unescapedText(translationKey),
        recommendationValue: value
      };
      renderedRecommendations.push( /*#__PURE__*/_jsx(RecommendationCard, {
        onClick: () => {
          trackInteraction('click homepage recommendation', {
            feature: parsedRecommendation.recommendationKey,
            item_type: parsedRecommendation.recommendationValue
          });
          push(fromFilterStateToLocation({
            [parsedRecommendation.recommendationKey]: [parsedRecommendation.recommendationValue]
          }, []));
        },
        recommendationKey: parsedRecommendation.recommendationKey,
        recommendationValue: parsedRecommendation.recommendationTranslatedValue
      }, `${parsedRecommendation.recommendationKey}-recommendation`));
      return renderedRecommendations;
    }, []);
  }, [normalizedUserInsights, buildRecommendationTranslationKey, trackInteraction]);
  return /*#__PURE__*/_jsxs(View, {
    "data-test-id": "recommended-for-you",
    paddingBlockEnd: "800",
    as: "div",
    children: [/*#__PURE__*/_jsx(H3, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.title"
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.recommendations.subtitle"
      })
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: getDataFetchingClientRequestState(insightsError, insightsLoading),
      ErrorComponent: GenericError,
      children: /*#__PURE__*/_jsx(CardGrid, {
        columnSize: 300,
        gutterSize: 32,
        children: renderedRecommendationCards
      })
    })]
  });
}